import { User } from "users/user";

/**
 * Capability questions a view may ask of the signed-in user, answered from the grants the user
 * carries rather than from who they are (issue #4651).
 *
 * Nothing is threaded through the call: `useSessionUser` attaches the session's grants to the user,
 * so a view asks `user?.canAccessWork()` and is done. A User that never went through that hook —
 * a row in a table, a reviewer on a proposal — carries no grants and answers false to all of it,
 * which is the safe answer to a question nobody has the standing to answer.
 *
 * Views used to reach for identity — `isSuperUser`, `isCompanyManager` — wherever a grant was the
 * real rule, which spent the whole value of tiered authorization: two people holding the same
 * resource at the same scope got different screens because one of them had a different label. Every
 * such question now lives here, phrased as "may this session do X", so a view never has to know
 * which resource, scope, or access level stands behind the answer.
 *
 * Identity has not disappeared, but it survives in exactly one place below — {@link actsForALegalEntity} —
 * with the reason written down.
 */

/**
 * Whether this session transacts on behalf of a legal entity at all.
 *
 * This is the one question permissions cannot answer. `UsersPermissionsService.GrantAllSuperUserPermissionsAsync`
 * mints Global Read/Write grants for every work resource a super user's own record carries, so a
 * super user passes `includesAccessToProposals` and `includesAccessToWork` outright. Those grants
 * exist so the platform's operators can administer the system, not so they can negotiate inside a
 * tenant's Work and Proposals desks — a super user has no counterparty standing and nothing they
 * saved there would belong to anyone. So the desks that commit an organization to something ask
 * this first.
 *
 * Nothing else in the application should branch on `isSuperUser` to decide what a person may do.
 */
function actsForALegalEntity(user: User): boolean {
  return user.isSuperUser !== true;
}

/**
 * This user seen as the holder of these grants, for `useSessionUser` to hand a view.
 *
 * A derived object rather than a mutation: the session's own User is shared by everything that
 * reads the session, and writing to it during a render is exactly the impurity the React Compiler
 * refuses. `Object.create` puts the grants on a fresh object whose prototype is this user, so the
 * name, the id and every other capability still resolve through it and nothing is copied.
 */
User.prototype.withSessionPermissions = function (permissions): User {
  const holder: User = Object.create(this);
  holder.sessionPermissions = permissions ?? undefined;
  return holder;
};

User.prototype.canAccessCommunications = function (): boolean {
  return this.sessionPermissions?.includesAccessToCommunications === true;
};

User.prototype.canAccessWork = function (): boolean {
  return actsForALegalEntity(this) && this.sessionPermissions?.includesAccessToWork === true;
};

User.prototype.canAccessProposals = function (): boolean {
  return actsForALegalEntity(this) && this.sessionPermissions?.includesAccessToProposals === true;
};

/**
 * Originating a proposal is a strictly higher bar than reading one: the API asserts Entity-Wide
 * Read/Write, because a proposal commits the firm to an offer.
 */
User.prototype.canCreateProposals = function (): boolean {
  return (
    this.canAccessProposals() &&
    this.sessionPermissions?.includesEntityWideReadWriteAccessToProposals === true
  );
};

/**
 * Whether this session may watch how far the firms its organization invited have got.
 *
 * Entity-Wide Read-Only on proposals, which is the client-side half of the board's rule: the list
 * of firms an organization is bringing on is a supplier list, not the proposals a member happens to
 * sit on. Staff reach the same board through platform administration instead, across every client.
 *
 * A super user is excluded by `actsForALegalEntity` for the usual reason — their Global grants are
 * for running the platform, and they have no firms of their own to be onboarding.
 */
User.prototype.canViewFirmOnboardingStatus = function (): boolean {
  return actsForALegalEntity(this) && this.sessionPermissions?.includesEntityWideAccessToProposals === true;
};

/**
 * Whether this session may see which templates its organization's roll-out proposals are built
 * from.
 *
 * The same entity-wide read of proposals the firm onboarding board asks for, and for the same
 * reason: this configuration sets the terms every firm the organization onboards is offered, which
 * is an entity-level thing to know rather than something a member learns from the proposals they
 * happen to sit on. It is what `GET work/client-proposal-templates` asserts.
 */
User.prototype.canViewClientProposalTemplates = function (): boolean {
  return actsForALegalEntity(this) && this.sessionPermissions?.includesEntityWideAccessToProposals === true;
};

/**
 * Whether this session may change them.
 *
 * Two grants, because the endpoint asserts two. Entity-wide read/write on proposals sets the terms
 * of an offer, and entity-wide read/write on discounts prices it — and the discount here is applied
 * to every firm the organization onboards, so somebody who may build proposals but not price them
 * must not acquire that by coming through this door.
 */
User.prototype.canConfigureClientProposalTemplates = function (): boolean {
  return (
    this.canViewClientProposalTemplates() &&
    this.sessionPermissions?.includesEntityWideReadWriteAccessToProposals === true &&
    this.sessionPermissions?.includesEntityWideReadWriteAccessToDiscounts === true
  );
};

User.prototype.canAccessTeams = function (): boolean {
  return this.sessionPermissions?.includesAccessToTeams === true;
};

/**
 * A team template is the organization's reusable staffing, so authoring one asks for Entity-Wide
 * Read/Write while listing asks only for Need-to-Know Read-Only (issue #4948).
 */
User.prototype.canCreateTeamTemplates = function (): boolean {
  return (
    this.canAccessTeams() && this.sessionPermissions?.includesEntityWideReadWriteAccessToTeams === true
  );
};

User.prototype.canAccessFeeSchedules = function (): boolean {
  return this.sessionPermissions?.includesAccessToFeeSchedules === true;
};

/**
 * `FeeScheduleTemplate.CreateAsync` asserts FeeSchedulesAccess at Need-to-Know and Read/Write, so
 * the access level alone is decisive here — unlike proposals and team templates, any scope will do.
 */
User.prototype.canCreateFeeScheduleTemplates = function (): boolean {
  return actsForALegalEntity(this) && this.sessionPermissions?.includesReadWriteAccessToFeeSchedules === true;
};

User.prototype.canAccessContracts = function (): boolean {
  return this.sessionPermissions?.includesAccessToContracts === true;
};

User.prototype.canAccessKnowledgeBase = function (): boolean {
  return this.sessionPermissions?.includesAccessToKnowledgeBase === true;
};

User.prototype.canAuthorKnowledgeBase = function (): boolean {
  return this.sessionPermissions?.includesWriteAccessToKnowledgeBase === true;
};

User.prototype.canAccessMarketplace = function (): boolean {
  return this.sessionPermissions?.includesReadAccessToMarketplace === true;
};

User.prototype.canSearchMarketplace = function (): boolean {
  return this.canAccessMarketplace() && this.sessionPermissions?.includesAccessToMarketplaceSearch === true;
};

User.prototype.canAccessNetwork = function (): boolean {
  return this.sessionPermissions?.includesAccessToNetwork === true;
};

/**
 * Administering the organization a session belongs to: its members, their permissions, and its
 * invitations. A super user administers the platform instead — see {@link User.canAdministerPlatform} —
 * and has no organization of their own to run.
 */
User.prototype.canAccessAdministration = function (): boolean {
  return actsForALegalEntity(this) && this.sessionPermissions?.includesAccessToAdministration === true;
};

/**
 * The one capability that is genuinely identity and not a grant. Platform administration is not a
 * resource any organization can hand out: it is the register a super user is entered into, and the
 * API gates the super-user endpoints on that register rather than on a stored permission.
 */
User.prototype.canAdministerPlatform = function (): boolean {
  return this.isSuperUser === true;
};
